import { MutableRefObject, useCallback, useRef } from 'react';

import { compareElementsByDocumentOrder } from './compareElementsByDocumentOrder';
import { useScrollContainerEvents } from './useScrollContainerEvents';

type ObservedSections = ReadonlyMap<string, { element: HTMLElement }>;

export type ScrollEndFallbackEvent = {
  id: string;
  type: 'reached-end' | 'left-end';
};

const SCROLL_END_TOLERANCE_PX = 1;

const containsRegisteredSection = (
  registrations: ObservedSections,
  scrollContainer: Element,
): boolean => {
  for (const { element } of registrations.values()) {
    if (scrollContainer.contains(element)) return true;
  }

  return false;
};

const isScrolledToEnd = (scrollContainer: Element): boolean =>
  scrollContainer.scrollTop + scrollContainer.clientHeight >=
  scrollContainer.scrollHeight - SCROLL_END_TOLERANCE_PX;

const getSectionsInScrollContainer = (
  registrations: ObservedSections,
  scrollContainer: Element,
): [string, { element: HTMLElement }][] =>
  Array.from(registrations.entries())
    .filter(([, { element }]) => scrollContainer.contains(element))
    .sort(([, first], [, second]) =>
      compareElementsByDocumentOrder(first.element, second.element),
    );

const getLastSectionId = (
  registrations: ObservedSections,
  scrollContainer: Element,
): string | undefined =>
  getSectionsInScrollContainer(registrations, scrollContainer).at(-1)?.[0];

const getSectionIdAtMarker = (
  registrations: ObservedSections,
  sectionIdsAtMarker: Set<string>,
  scrollContainer: Element,
): string | undefined =>
  getSectionsInScrollContainer(registrations, scrollContainer).find(([id]) =>
    sectionIdsAtMarker.has(id),
  )?.[0];

type UseScrollEndFallbackOptions = {
  onFallback: (event: ScrollEndFallbackEvent) => void;
  registrations: ObservedSections;
  sectionIdsAtMarker: Set<string>;
};

export const useScrollEndFallback = ({
  onFallback,
  registrations,
  sectionIdsAtMarker,
}: UseScrollEndFallbackOptions): MutableRefObject<boolean> => {
  const scrollContainersAtEndRef = useRef(new WeakSet<Element>());
  const fallbackActiveRef = useRef(false);

  const processScrollContainer = useCallback(
    (scrollContainer: Element): void => {
      if (isScrolledToEnd(scrollContainer)) {
        if (scrollContainersAtEndRef.current.has(scrollContainer)) return;

        const lastSectionId = getLastSectionId(registrations, scrollContainer);
        if (!lastSectionId) return;

        scrollContainersAtEndRef.current.add(scrollContainer);
        fallbackActiveRef.current = true;
        onFallback({ id: lastSectionId, type: 'reached-end' });
        return;
      }

      if (!scrollContainersAtEndRef.current.delete(scrollContainer)) return;

      fallbackActiveRef.current = false;
      const sectionIdAtMarker = getSectionIdAtMarker(
        registrations,
        sectionIdsAtMarker,
        scrollContainer,
      );
      if (sectionIdAtMarker) {
        onFallback({ id: sectionIdAtMarker, type: 'left-end' });
      }
    },
    [onFallback, registrations, sectionIdsAtMarker],
  );
  const acceptScrollContainer = useCallback(
    (scrollContainer: Element): boolean =>
      containsRegisteredSection(registrations, scrollContainer),
    [registrations],
  );
  useScrollContainerEvents({
    acceptScrollContainer,
    onScrollContainer: processScrollContainer,
  });

  return fallbackActiveRef;
};
